import { FormikErrors, FormikProvider, useFormik } from 'formik';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';

import { useErrorBoundary, useFeature, useLocalize } from '@treams/treams-hooks';
import { useFeatureNeedsViewerApproval } from '@treams/treams-hooks/src/useFeatureNeedsViewerApproval';
import { ConversationIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Avatar,
	FlexGap,
	GraphQLErrorMessage,
	Heading,
	Label,
	Paragraph,
	QuillText,
	Title,
	tokens,
} from '@treams/treams-ui';

import { EvaluationSubmissionReveal } from '../../reveal';
import EvaluationErrorCard from '../EvaluationErrorCard';
import EvaluationSubmissionScore from '../EvaluationSubmissionScore';
import EvaluationSubmissionSection from '../EvaluationSubmissionSection';
import EvaluationSubmissionUnsign from '../EvaluationSubmissionUnsign';
import { useAIInsightsContext } from '../contexts/AIInsightsContext';
import useDetectPrint from '../hooks/useDetectPrint';
import { EvaluationSubmissionAIInsightsFeedback } from './EvaluationSubmissionAIInsightsFeedback';
import EvaluationSubmissionFooter from './EvaluationSubmissionFooter';
import EvaluationSubmissionItem from './EvaluationSubmissionItem';
import EvaluationSubmissionSignatureModal from './EvaluationSubmissionSignatureModal';
import EvaluationSubmissionStatus from './EvaluationSubmissionStatus';
import EvaluationSubmissionTranscription from './EvaluationSubmissionTranscription';
import LayoutSection from './layout/LayoutSection';
import EvaluationNotes from './notes/EvaluationNotes';

const AUTO_SAVE_DEBOUNCE_IN_MS = 1500;

const TitleIcon = styled(Box)`
	width: 40px;
	height: 40px;
	flex-shrink: 0;

	svg {
		display: block;
		width: 40px;
		height: 40px;

		path {
			fill: ${tokens.icon.primary};
		}
	}
`;

const Divider = styled(Box)`
	height: 1px;
	width: 100%;
	background: ${tokens.border.subtle};
`;

function isSubmissionItemUnanswered(item: graphql.EvaluationSubmissionItem): boolean {
	if (item.type === graphql.EvaluationSectionItemType.OpenQuestion) {
		return (
			!item.openQuestion ||
			item.openQuestion.ops
				.map((op: { insert?: string }) => op.insert ?? '')
				.join('')
				.trim().length === 0
		);
	}

	if (item.type === graphql.EvaluationSectionItemType.Sentiment) {
		return item.sentiment === null || item.sentiment === undefined;
	}

	if (item.type === graphql.EvaluationSectionItemType.Stars) {
		return item.stars === null || item.stars === undefined;
	}

	if (item.type === graphql.EvaluationSectionItemType.Competency) {
		return item.competency === null || item.competency === undefined;
	}

	if (item.type === graphql.EvaluationSectionItemType.ReviewGoals) {
		return (
			!!item.reviewGoals &&
			item.reviewGoals.length > 0 &&
			Boolean(
				item.reviewGoals.find((goal) => goal.competency === null || goal.competency === undefined),
			)
		);
	}

	return false;
}

function isSubmissionItemRequiredForViewer(
	submissionItemFilledInBy: graphql.EvaluationFillInBy,
	viewerParty: graphql.EvaluationSubmissionParty,
) {
	if (submissionItemFilledInBy === graphql.EvaluationFillInBy.Both) {
		return true;
	}

	if (submissionItemFilledInBy === graphql.EvaluationFillInBy.Employee) {
		return viewerParty === graphql.EvaluationSubmissionParty.Employee;
	}

	if (submissionItemFilledInBy === graphql.EvaluationFillInBy.Manager) {
		return viewerParty === graphql.EvaluationSubmissionParty.Manager;
	}
}

export default function EvaluationSubmission(props: {
	submission: graphql.EvaluationSubmissionFragment;
	onRefresh: () => void;
}) {
	const [updateEvaluationSubmission, { loading: updateLoading, error: updateError }] =
		graphql.useUpdateEvaluationSubmissionMutation({});

	const [shareEvaluationSubmission, { loading: shareLoading }] =
		graphql.useMarkEvaluationSubmissionSharedMutation({
			variables: { input: { id: props.submission.id } },
			// Sharing can make the reveal visible to the employee; refetch so
			// the reveal card switches from locked to visible without reload.
			refetchQueries: [graphql.EvaluationRevealDocument],
		});

	const [signEvaluationSubmission, { error: signError }] =
		graphql.useSignEvaluationSubmissionMutation({
			refetchQueries: [graphql.EvaluationRevealDocument],
		});

	const mutationError = updateError || signError;

	const { localize } = useLocalize();
	const isPrinting = useDetectPrint();
	const { t } = useTranslation();
	const [lastSaved, setLastSaved] = useState<Date | null>(null);
	const [error, attempt] = useErrorBoundary();

	const signScrollPositionRef = useRef<HTMLSpanElement | null>(null);
	const [flashSignSection, setFlashSignSection] = useState(false);

	const itemRefs = useRef<(HTMLSpanElement | null)[][]>([]);

	const [showSignModal, setShowSignModal] = useState(false);
	const [signChecked, setSignChecked] = useState(false);
	const lastSubmitWasSignRef = useRef(false);

	const { showAIInsightsFeedback } = useAIInsightsContext();

	const hasTranscriptions = useFeature(graphql.FeatureName.ConversationTranscriptions);
	const conversationAIInsightsEnabled = useFeature(graphql.FeatureName.ConversationAiInsights);
	const { needsViewerApproval: viewerNeedsAIInsightsApproval } = useFeatureNeedsViewerApproval(
		graphql.FeatureName.ConversationAiInsights,
	);

	const initialValues = {
		sections: props.submission.viewer.sections.map((section) => ({
			items: section.items.map(
				(item) =>
					item && {
						type: item.type,
						stars: item.stars,
						openQuestion: item.openQuestion,
						sentiment: item.sentiment,
						multipleChoice: item.multipleChoice,
						competency: item.competency,
						reviewGoals: item.reviewGoals?.map((goal) => _.omit(goal, '__typename')),
					},
			),
		})),
		signed: Boolean(props.submission.viewer.hasSigned),
	};

	// We need to keep track of the values in a ref because the formik form will reset after a
	// submit to reset the dirty state but the values of onSubmit handlers are debounced values
	const valuesRef = useRef(initialValues);

	// We need to track the current queue of changes because of the debounced values and the
	// different ways the user can interact with the form (e.g. autosave, save, sign)
	const valuesQueueRef = useRef<(typeof form.values)[]>([]);

	const form = useFormik({
		initialValues,
		// Don't validate the change as we don't use validation in the form.
		// Validation causes slowdowns when typing at a fast pace.
		// See https://github.com/jaredpalmer/formik/issues/342#issuecomment-713734191
		validateOnChange: false,
		onSubmit: async (values, { submitForm, setSubmitting, resetForm }) => {
			valuesQueueRef.current.push(values);
			lastSubmitWasSignRef.current = values.signed;

			if (values.signed) {
				const errors = {
					sections: values.sections.map((section, sectionIndex) => ({
						items: section.items.map((item, itemIndex) => {
							const submissionItem =
								props.submission.evaluationForm.sections[sectionIndex].items[itemIndex];

							if (!submissionItem || !item) {
								return null;
							}

							if (submissionItem.optional) {
								return null;
							}

							const isRequiredForViewer = isSubmissionItemRequiredForViewer(
								submissionItem.fillInBy,
								props.submission.viewer.party,
							);

							return isRequiredForViewer && isSubmissionItemUnanswered(item)
								? `Answer for ${item.type} is missing`
								: null;
						}),
					})),
				};

				form.setErrors(errors as unknown as FormikErrors<typeof initialValues>);
			}

			if (updateLoading) {
				return;
			}

			try {
				const sectionsUnchanged = _.isEqual(values.sections, initialValues.sections);
				const signedChanged = values.signed !== initialValues.signed;
				const onlySignedChanged = sectionsUnchanged && signedChanged;

				if (onlySignedChanged && values.signed) {
					await signEvaluationSubmission({
						variables: {
							input: {
								id: props.submission.id,
								party: props.submission.viewer.party,
							},
						},
					});

					return;
				}

				await updateEvaluationSubmission({
					variables: {
						input: {
							id: props.submission.id,
							sections: values.sections,
							signed: values.signed,
						},
					},
					onCompleted: () => {
						setLastSaved(new Date());

						valuesQueueRef.current = valuesQueueRef.current.filter(
							(queuedValues) => queuedValues !== values,
						);

						if (valuesQueueRef.current.length > 0) {
							submitForm();
						} else {
							resetForm({ values: valuesRef.current });
							setSubmitting(false);
						}
					},
				});
			} catch {
				// updateEvaluationSubmission call throws error but also leaves an error state.
				// We ignore the thrown error and use the error state.
			}
		},
	});

	const handleSign = () => {
		form.setFieldValue('signed', true);
		form.submitForm();
	};

	const handleSave = attempt(async () => {
		await form.submitForm();
	});

	const handleAutoSave = useDebouncedCallback(handleSave, AUTO_SAVE_DEBOUNCE_IN_MS);

	const shouldAutoSave = !props.submission.viewer.hasShared;

	const scrollToSignSectionAndFlash = () => {
		if (!signScrollPositionRef.current) {
			return;
		}

		signScrollPositionRef.current.scrollIntoView({ behavior: 'smooth' });

		setFlashSignSection(true);

		setTimeout(() => {
			setFlashSignSection(false);
		}, 1200);
	};

	const attemptSignSubmission = () => {
		if (signChecked) {
			setShowSignModal(true);

			return;
		}

		scrollToSignSectionAndFlash();
	};

	useEffect(() => {
		// After every value change we need to update the current ref value
		valuesRef.current = form.values;
		if (props.submission.viewer.viewerCanUpdate && form.dirty && shouldAutoSave) {
			handleAutoSave();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [form.values, shouldAutoSave]);

	const isSignError = signError || (updateError && lastSubmitWasSignRef.current);

	// Scroll to first item with error if a sign attempt fails
	useEffect(() => {
		if (
			!isSignError ||
			!itemRefs.current ||
			!form.errors.sections ||
			typeof form.errors.sections === 'string'
		) {
			return;
		}

		const firstSectionWithErrorIndex = form.errors.sections.findIndex((section) =>
			section.items.some((item) => typeof item === 'string'),
		);

		const firstSectionWithError = form.errors.sections[firstSectionWithErrorIndex];

		if (
			!firstSectionWithError ||
			typeof firstSectionWithError === 'string' ||
			!firstSectionWithError.items ||
			typeof firstSectionWithError.items === 'string'
		) {
			return;
		}

		const firstItemWithErrorIndex =
			firstSectionWithErrorIndex !== -1
				? firstSectionWithError.items?.findIndex((item) => typeof item === 'string')
				: -1;

		if (firstSectionWithErrorIndex < 0 || !firstItemWithErrorIndex || firstItemWithErrorIndex < 0) {
			return;
		}

		const firstItemWithErrorRef =
			itemRefs.current[firstSectionWithErrorIndex][firstItemWithErrorIndex];

		if (!firstItemWithErrorRef) {
			return;
		}

		firstItemWithErrorRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}, [isSignError, form.errors.sections]);

	const filteredSections = props.submission.evaluationForm.sections
		.map((section, index) => ({ section, index }))
		.filter(({ section }) =>
			section.items.some((item) => {
				if (
					item.type === graphql.EvaluationSectionItemType.FinalScore ||
					item.type === graphql.EvaluationSectionItemType.Signature
				) {
					return false;
				}

				if (props.submission.employee.hasShared || props.submission.manager.hasShared) {
					return true;
				}

				return (
					item.fillInBy === graphql.EvaluationFillInBy.Both ||
					item.fillInBy.valueOf() === props.submission.viewer.party.valueOf()
				);
			}),
		);

	return (
		<FormikProvider value={form}>
			<LayoutSection>
				{error && (
					<EvaluationErrorCard
						error={error}
						submission={props.submission as graphql.EvaluationSubmission}
						lastSaved={lastSaved}
					/>
				)}

				{mutationError && (
					<GraphQLErrorMessage error={mutationError.graphQLErrors[0]} warning={!!isSignError} />
				)}
			</LayoutSection>
			<LayoutSection>
				<span ref={signScrollPositionRef}></span>
				<FlexGap flexDirection="column" gap={tokens.gap.section} width="100%">
					<FlexGap flexDirection="column" gap={tokens.gap.group}>
						<Flex alignItems="center" style={{ gap: tokens.gap.list }}>
							<TitleIcon>
								<ConversationIcon />
							</TitleIcon>
							<Title>{localize(props.submission.evaluationForm.title).text}</Title>
						</Flex>
						{props.submission.evaluationForm.description && (
							<QuillText value={localize(props.submission.evaluationForm.description).text} />
						)}
					</FlexGap>
					<FlexGap gap={tokens.gap.page} flexWrap="wrap" alignItems="center">
						<FlexGap flexDirection="column" gap={tokens.gap.inline} justifyContent="center">
							<Label>{t('evaluations.submission.about.label')}</Label>
							<FlexGap gap={tokens.gap.inline} alignItems="center">
								<Avatar size={16} user={props.submission.employee.user} flexShrink={0} />
								<Paragraph>
									{`${props.submission.employee.user.firstName} ${props.submission.employee.user.lastName}`}
								</Paragraph>
							</FlexGap>
						</FlexGap>
						{props.submission.moment && (
							<FlexGap flexDirection="column" gap={tokens.gap.inline}>
								<Label>{t('evaluations.submission.moment.label')}</Label>
								<Paragraph>{localize(props.submission.moment.momentTemplate.title).text}</Paragraph>
							</FlexGap>
						)}
					</FlexGap>
					<Divider />
					<EvaluationSubmissionStatus
						submission={props.submission}
						updateLoading={updateLoading}
						flashSignSection={flashSignSection}
						signChecked={signChecked}
						setSignChecked={setSignChecked}
						setShowSignModal={setShowSignModal}
					/>
				</FlexGap>
				<Divider my={`${tokens.gap.page}px`} />
				{hasTranscriptions && conversationAIInsightsEnabled && !viewerNeedsAIInsightsApproval && (
					<EvaluationSubmissionTranscription
						submission={props.submission}
						onRefresh={props.onRefresh}
					/>
				)}
				{filteredSections.map(({ section, index: sectionIndex }) => (
					<EvaluationSubmissionSection key={sectionIndex} mb={4}>
						{section.items.map((_, index) => {
							const hasError = (): boolean => {
								const sectionErrors = form.errors.sections?.[sectionIndex];

								if (sectionErrors && typeof sectionErrors !== 'string') {
									const itemError = sectionErrors.items?.[index];

									return typeof itemError === 'string';
								}

								return false;
							};

							return (
								<React.Fragment key={index}>
									{/* scrollTo anchor ref */}
									<span
										key={index}
										ref={(element) => {
											if (!itemRefs.current[sectionIndex]) {
												itemRefs.current[sectionIndex] = [];
											}
											itemRefs.current[sectionIndex][index] = element;
										}}
									/>
									<EvaluationSubmissionItem
										section={sectionIndex}
										item={index}
										isLastItemOfSection={index === section.items.length - 1}
										submission={props.submission as graphql.EvaluationSubmission}
										onSave={handleSave}
										isSaving={updateLoading}
										showMissingRequiredFieldError={isSignError && hasError()}
									/>
								</React.Fragment>
							);
						})}
					</EvaluationSubmissionSection>
				))}
				<EvaluationSubmissionScore
					submission={props.submission as graphql.EvaluationSubmission}
					saving={updateLoading}
				/>
				<EvaluationSubmissionReveal submission={props.submission} />
				{(props.submission.viewer.hasShared || !props.submission.viewer.viewerCanUpdate) &&
					props.submission.comments.some(
						(comment) => !comment.metadata?.some(({ key }) => key === 'formItemID'),
					) && (
						<Box mb={4}>
							<Box mb={2}>
								<Heading>{t('evaluation.submission.notes.title')}</Heading>
							</Box>
							<EvaluationNotes submission={props.submission} />
						</Box>
					)}
				{!isPrinting && (
					<>
						<EvaluationSubmissionUnsign
							submission={props.submission as graphql.EvaluationSubmission}
						/>
						<EvaluationSubmissionFooter
							submission={props.submission as graphql.EvaluationSubmission}
							onShare={shareEvaluationSubmission}
							onSign={attemptSignSubmission}
							onSave={handleSave}
							sharing={shareLoading}
							saving={updateLoading}
							error={error}
							mutationError={!isSignError ? mutationError : undefined}
							lastSaved={lastSaved}
						/>
					</>
				)}
				{showAIInsightsFeedback && (
					<EvaluationSubmissionAIInsightsFeedback submissionID={props.submission.id} />
				)}
				{showSignModal && (
					<EvaluationSubmissionSignatureModal
						submission={props.submission as graphql.EvaluationSubmission}
						saving={updateLoading}
						onSign={handleSign}
						onDismiss={() => setShowSignModal(false)}
					/>
				)}
			</LayoutSection>
		</FormikProvider>
	);
}
